
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

// material-ui
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// icons
import BellOutlined from "@ant-design/icons/BellOutlined";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import CloseCircleOutlined from "@ant-design/icons/CloseCircleOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";

// project
import MainCard from "components/MainCard";
import IconButton from "components/@extended/IconButton";
import Transitions from "components/@extended/Transitions";

import api from "services/axiosInstance";

const avatarSX = {
  width: 36,
  height: 36,
  fontSize: "1rem"
};

const getNotifIcon = (type) => {
  switch (type) {
    case "approved":
      return {
        icon: <CheckOutlined />,
        color: "success.main",
        bg: "success.lighter"
      };

    case "revision":
      return {
        icon: <EditOutlined />,
        color: "warning.main",
        bg: "warning.lighter"
      };

    case "rejected":
      return {
        icon: <CloseCircleOutlined />,
        color: "error.main",
        bg: "error.lighter"
      };

    default:
      return {
        icon: <BellOutlined />,
        color: "primary.main",
        bg: "primary.lighter"
      };
  }
};

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const anchorRef = useRef(null);
  const fetchingRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = async () => {
    // Hindari request notifikasi yang berjalan bersamaan.
    if (fetchingRef.current) return;

    // Jangan polling jika tab browser sedang tidak aktif.
    if (document.visibilityState !== "visible") return;

    fetchingRef.current = true;

    try {
      const res = await api.get("/api/aktivitas/notifications");
      setNotifications(res.data.data || []);
    } catch (err) {
      console.error("Gagal load notif", err);
    } finally {
      fetchingRef.current = false;
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/api/aktivitas/notifications/read");

      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          is_read: true
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    // Ambil notifikasi saat komponen pertama kali aktif.
    fetchNotifications();

    // Kurangi polling dari 10 detik menjadi 60 detik.
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
      }
    }, 60000);

    // Ambil kembali notifikasi ketika tab menjadi aktif.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, []);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        ref={anchorRef}
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={downMD ? "bottom" : "bottom-end"}
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={downMD ? "top" : "top-right"}
            in={open}
            {...TransitionProps}
          >
            <Paper sx={{ minWidth: 350, maxWidth: 420 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  content={false}
                  secondary={
                    unreadCount > 0 && (
                      <Tooltip title="Mark all as read">
                        <IconButton
                          color="success"
                          size="small"
                          onClick={markAllRead}
                        >
                          <CheckCircleOutlined />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <List sx={{ p: 0 }}>
                    {notifications.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Belum ada notifikasi"
                          secondary="Semua tenang di sini ??"
                        />
                      </ListItem>
                    )}

                    {notifications.map((notif) => {
                      const meta = getNotifIcon(notif.type);

                      return (
                        <ListItem
                          key={notif.id}
                          component={ListItemButton}
                          divider
                          selected={!notif.is_read}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                ...avatarSX,
                                color: meta.color,
                                bgcolor: meta.bg
                              }}
                            >
                              {meta.icon}
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            disableTypography
                            primary={
                              <Typography variant="subtitle1" component="div">
                                {notif.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" component="div">
                                  {notif.message}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="span"
                                >
                                  {dayjs(notif.created_at).format(
                                    "DD MMM YYYY HH:mm"
                                  )}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
